import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { IconCheck } from '@/icons';
import Button from '../core/Button';
import { BearerSelection } from '../BearerSelection';
import {
  useSelfServiceMoveTicket,
  useSelfServiceLendTicket,
  getSelfServiceListTicketsQueryKey,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast';
import { useFavoredBearer } from '@/hooks/useFavoredBearer';
import type { BearerListItem } from '@/hooks/useBearerMapping';
import { WarningBanner } from '../core/WarningBanner';

interface ChangeBearerDialogProps {
  ticketId: string;
  travellerId: string;
  bearerItems: BearerListItem[];
  currentBearerId?: string;
  mostRecentBobcatBearerId?: string;
  isLoaned: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChangeBearerDialog = ({
  ticketId,
  travellerId,
  bearerItems,
  currentBearerId,
  mostRecentBobcatBearerId,
  isLoaned,
  onOpenChange,
}: ChangeBearerDialogProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const [selectedBearerId, setSelectedBearerId] = useState('');

  const moveTicket = useSelfServiceMoveTicket();
  const lendTicket = useSelfServiceLendTicket();
  const { setFavoredBearer, getFavoredBearerErrorMessage, isPending } =
    useFavoredBearer({ travellerId, isSelfService: true });

  useEffect(() => {
    if (!selectedBearerId && currentBearerId) {
      setSelectedBearerId(currentBearerId);
    }
  }, [selectedBearerId, currentBearerId]);

  const handleCancel = useCallback(() => {
    setSelectedBearerId('');
    onOpenChange(false);
  }, [onOpenChange]);

  const mutation = isLoaned ? lendTicket : moveTicket;

  const handleSubmit = useCallback(async () => {
    if (!selectedBearerId) return;

    await mutation.mutateAsync(
      {
        ticketID: ticketId,
        data: { mtb_bearer_id: selectedBearerId },
      },
      {
        onSuccess: async () => {
          let favoredBearerUpdated = true;

          if (selectedBearerId !== mostRecentBobcatBearerId) {
            try {
              await setFavoredBearer(selectedBearerId, { showToast: false });
            } catch (error) {
              favoredBearerUpdated = false;
              toast.error(
                {
                  title: formatMessage({
                    defaultMessage: 'Kunde inte uppdatera föredragen bärare',
                  }),
                  description: getFavoredBearerErrorMessage(error),
                },
                { autoClose: 3000 },
              );
            }
          }

          if (favoredBearerUpdated) {
            toast.success(
              {
                title: formatMessage({
                  defaultMessage: 'Bärare uppdaterad.',
                }),
              },
              { autoClose: 4000 },
            );
          }
          await queryClient.invalidateQueries({
            queryKey: getSelfServiceListTicketsQueryKey(),
          });
          setSelectedBearerId('');
          onOpenChange(false);
        },
        onError: (err) => {
          const message =
            isAxiosError(err) && err.response?.data?.message
              ? err.response.data.message
              : formatMessage({
                  defaultMessage:
                    'Försök spara igen senare, eller vänligen kontakta support.',
                });
          toast.error(
            {
              title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
              description: message,
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  }, [
    selectedBearerId,
    ticketId,
    mutation,
    queryClient,
    formatMessage,
    onOpenChange,
    mostRecentBobcatBearerId,
    setFavoredBearer,
    getFavoredBearerErrorMessage,
  ]);

  return (
    <>
      <WarningBanner className="mb-4">
        <FormattedMessage
          id="page.traveller.changeBearerWarning"
          defaultMessage="När man byter bärare ändras vilken bärare som är Föredragen. Alla automatiskt utställda biljetter (t.ex. skolbiljetter) kommer automatiskt att hamna på den nya Föredragna bäraren framöver."
        />
      </WarningBanner>
      <BearerSelection
        bearers={bearerItems}
        onBearerSelect={setSelectedBearerId}
        selectedBearer={selectedBearerId}
        mostRecentBobcatBearerId={mostRecentBobcatBearerId}
      />
      <div className="mt-4 flex w-full flex-wrap justify-end gap-2">
        <Button onClick={handleCancel} variant="ghost">
          <FormattedMessage defaultMessage="Avbryt" />
        </Button>
        <Button
          startIcon={<IconCheck />}
          variant="filled"
          disabled={!selectedBearerId || selectedBearerId === currentBearerId}
          loading={mutation.isPending || isPending}
          onClick={handleSubmit}
        >
          <FormattedMessage defaultMessage="Byt bärare" />
        </Button>
      </div>
    </>
  );
};
